import LoadingButton from '@mui/lab/LoadingButton/LoadingButton'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  IconButton,
} from '@mui/material'
import React from 'react'
import { styled } from '@mui/material/styles'
import { SelectChangeEvent } from '@mui/material/Select/SelectInput'
import { useSnackbars } from '@assembly/hooks'
import { getApiErrorMsg, getMediaUrl } from '@assembly/utils'
import { SnackbarType } from '@assembly/contexts'
import { Candidate, CandidateState, EvaluationState } from './types'
import { CircularProgressWithLabel, InfoItem, AvatarUpload } from '@assembly/components'
import isEmail from 'validator/lib/isEmail'
import { updateCandidate, uploadMedia } from '@assembly/api/candidates'
import { Media } from '@assembly/shared/types/media'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'

const StyledTextField = styled(TextField)(({ }) => ({
  margin: 0,
  marginBottom: '20px',
  width: '100%',
}))

const StyledFormControl = styled(FormControl)(({ }) => ({
  margin: 0,
  marginBottom: '20px',
  width: '100%',
}))

const MediaButtonBox = styled(Box)(() => ({
  display: 'flex',
  gap: '10px',
  marginBottom: '20px',
}))

const UploadProgressIndicatorBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
}))

const SectionTitle = styled(Typography)(() => ({
  fontSize: '20px',
  marginBottom: '10px',
  fontWeight: 500,
}))

type EditCandidateDialogProps = {
  open: boolean
  onClose: () => void
  candidate: Candidate | null
  onSuccess: (candidate: Candidate) => void
}

type InitialFormValues = {
  firstName: string
  lastName: string
  email: string
  notes: string
  referral: string
  resume: Media | null
  resumeLink: string
  source: string
  evaluationState: string
  state: string
  expectedCompUSD: number
  invalid_firstName: boolean
  invalid_lastName: boolean
  invalid_email: boolean
  bio: string
  profilePicture: Media | null
  portfolio: Media[]
  assessmentResponses: { [key: string]: any }
}

export default function EditCandidateDialog({
  open,
  onClose,
  candidate,
  onSuccess,
}: EditCandidateDialogProps) {
  const initialFormValues: InitialFormValues = {
    firstName: '',
    lastName: '',
    email: '',
    notes: '',
    referral: '',
    resume: null,
    resumeLink: '',
    source: '',
    evaluationState: '',
    state: '',
    expectedCompUSD: 0,
    invalid_firstName: false,
    invalid_lastName: false,
    invalid_email: false,
    bio: '',
    profilePicture: null,
    portfolio: [],
    assessmentResponses: {},
  }
  const [formValues, setFormValues] = React.useState(initialFormValues)
  const [loading, setIsLoading] = React.useState<boolean>(false)
  const { addAlert } = useSnackbars()
  const [isMediaUploading, setIsMediaUploading] = React.useState<boolean>(false)
  const [uploadProgress, setUploadProgress] = React.useState<number>(0)
  const [isProfilePictureUploading, setIsProfilePictureUploading] = React.useState<boolean>(false)
  const [isPortfolioUploading, setIsPortfolioUploading] = React.useState<boolean>(false)
  const [isAssessmentResponseUploading, setIsAssessmentResponseUploading] = React.useState<boolean>(false)
  const [uploadingAssessmentResponse, setUploadingAssessmentResponse] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (open && candidate) {
      setFormValues({
        ...formValues,
        firstName: candidate.firstName,
        lastName: candidate.lastName,
        email: candidate.email,
        notes: candidate.notes || '',
        referral: candidate.referral || '',
        resumeLink: candidate.resumeLink || '',
        source: candidate.source || '',
        evaluationState: candidate.evaluationState || '',
        state: candidate.state || '',
        expectedCompUSD: candidate.expectedCompUSD || 0,
        resume: candidate.resume as Media,
        bio: candidate.bio || '',
        profilePicture: candidate.profilePicture,
        portfolio: candidate.portfolio || [],
        assessmentResponses: candidate.assessmentResponses || {},
      })
    }
  }, [candidate])

  const handleChangeFormField = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormValues({ ...formValues, [name]: value, [`invalid_${name}`]: false })
  }

  const handleChangeSelect = (event: SelectChangeEvent) => {
    const { name, value } = event.target
    setFormValues({
      ...formValues,
      [name]: value,
      [`invalid_${name}`]: false,
    })
  }

  const handleClose = () => {
    setFormValues(initialFormValues)
    onClose()
  }

  const handleChangeUploadMedia = async (event: any) => {
    const formData = new FormData()
    formData.append('file', event.target.files[0])
    try {
      setIsMediaUploading(true)
      const { data } = await uploadMedia(formData, (progress: any) => {
        setUploadProgress(progress)
      })
      setFormValues({
        ...formValues,
        resume: data,
      })
      setIsMediaUploading(false)

      setUploadProgress(0)
    } catch (error: any) {
      setIsMediaUploading(false)
      addAlert({
        message: getApiErrorMsg(error) || 'Error Uploading Media',
        type: SnackbarType.Error,
      })
    }
  }

  const handleChangeProfilePicture = async (event: any) => {
    const formData = new FormData()
    formData.append('file', event.target.files[0])
    try {
      setIsProfilePictureUploading(true)
      const { data } = await uploadMedia(formData, (progress: any) => {
        setUploadProgress(progress)
      })
      setFormValues({
        ...formValues,
        profilePicture: data,
      })
      setIsProfilePictureUploading(false)
      setUploadProgress(0)
    } catch (error: any) {
      setIsProfilePictureUploading(false)
      addAlert({
        message: getApiErrorMsg(error) || 'Error Uploading Media',
        type: SnackbarType.Error,
      })
    }
  }

  const handleRemoveProfilePicture = () => {
    setFormValues({
      ...formValues,
      profilePicture: null,
    })
  }

  const handleAddPortfolio = () => {
    // This will be handled by the file upload directly
  }

  const handleRemovePortfolio = (index: number) => {
    const newPortfolio = [...formValues.portfolio]
    newPortfolio.splice(index, 1)
    setFormValues({
      ...formValues,
      portfolio: newPortfolio,
    })
  }

  const handlePortfolioMediaUpload = async (event: any) => {
    const formData = new FormData()
    formData.append('file', event.target.files[0])
    try {
      setIsPortfolioUploading(true)
      const { data } = await uploadMedia(formData, (progress: any) => {
        setUploadProgress(progress)
      })
      // Ensure we only add valid Media objects
      if (data && typeof data === 'object' && 'id' in data && 'name' in data && 'media' in data) {
        setFormValues({
          ...formValues,
          portfolio: [...formValues.portfolio, data],
        })
      }
      setIsPortfolioUploading(false)
      setUploadProgress(0)
    } catch (error: any) {
      setIsPortfolioUploading(false)
      addAlert({
        message: getApiErrorMsg(error) || 'Error Uploading Media',
        type: SnackbarType.Error,
      })
    }
  }

  const handleAssessmentResponseMediaUpload = async (event: any, key: string) => {
    const formData = new FormData()
    formData.append('file', event.target.files[0])
    try {
      setUploadingAssessmentResponse(key)
      const { data } = await uploadMedia(formData, (progress: any) => {
        setUploadProgress(progress)
      })
      setFormValues({
        ...formValues,
        assessmentResponses: {
          ...formValues.assessmentResponses,
          [key]: {
            ...formValues.assessmentResponses[key],
            videoResponse: data
          }
        },
      })
      setUploadingAssessmentResponse(null)
      setUploadProgress(0)
    } catch (error: any) {
      setUploadingAssessmentResponse(null)
      addAlert({
        message: getApiErrorMsg(error) || 'Error Uploading Media',
        type: SnackbarType.Error,
      })
    }
  }

  const handleAddAssessmentResponse = () => {
    const newKey = `new_${Date.now()}`
    // Get jobID from existing assessment responses if available
    const existingJobID = Object.values(formValues.assessmentResponses)[0]?.jobID || ''

    setFormValues({
      ...formValues,
      assessmentResponses: {
        ...formValues.assessmentResponses,
        [newKey]: {
          skillsAssessmentId: newKey,
          jobID: existingJobID,
          title: 'New Assessment Response',
          videoResponse: null,
          videoTranscript: null,
          textResponse: null,
          type: 'VIDEO',
          featured: false
        }
      }
    })
  }

  const handleToggleFeatured = (key: string) => {
    const newResponses = { ...formValues.assessmentResponses }
    // First, set all responses to not featured
    Object.keys(newResponses).forEach(k => {
      newResponses[k] = {
        ...newResponses[k],
        featured: false
      }
    })
    // Then set the selected one to featured
    newResponses[key] = {
      ...newResponses[key],
      featured: true
    }

    // Reorder responses to put featured first
    const orderedResponses = Object.entries(newResponses)
      .sort(([, a], [, b]) => {
        if (a.featured) return -1
        if (b.featured) return 1
        return 0
      })
      .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {})

    setFormValues({
      ...formValues,
      assessmentResponses: orderedResponses
    })
  }

  const handleRemoveAssessmentResponse = (key: string) => {
    const newResponses = { ...formValues.assessmentResponses }
    delete newResponses[key]
    setFormValues({
      ...formValues,
      assessmentResponses: newResponses
    })
  }

  const handleAssessmentResponseTitleChange = (key: string, newTitle: string) => {
    setFormValues({
      ...formValues,
      assessmentResponses: {
        ...formValues.assessmentResponses,
        [key]: {
          ...formValues.assessmentResponses[key],
          title: newTitle
        }
      }
    })
  }

  const handleAssessmentResponseTextChange = (key: string, newText: string) => {
    setFormValues({
      ...formValues,
      assessmentResponses: {
        ...formValues.assessmentResponses,
        [key]: {
          ...formValues.assessmentResponses[key],
          textResponse: newText
        }
      }
    })
  }

  const handleClickSave = async () => {
    if (
      formValues.firstName.length === 0 ||
      formValues.lastName.length === 0 ||
      formValues.email.length === 0 ||
      !isEmail(formValues.email)
    ) {
      setFormValues({
        ...formValues,
        invalid_firstName: formValues.firstName.length === 0,
        invalid_lastName: formValues.lastName.length === 0,
        invalid_email:
          formValues.email.length === 0 || !isEmail(formValues.email),
      })
      return
    }

    const payload = {
      ...candidate,
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      email: formValues.email,
      notes: formValues.notes,
      referral: formValues.referral,
      resumeLink: formValues.resumeLink,
      source: formValues.source,
      evaluationState: formValues.evaluationState,
      state: formValues.state,
      expectedCompUSD: formValues.expectedCompUSD,
      resume: formValues.resume,
      bio: formValues.bio,
      profilePicture: formValues.profilePicture,
      portfolio: formValues.portfolio as Media[],
      assessmentResponses: formValues.assessmentResponses,
    }

    try {
      setIsLoading(true)
      const { data } = await updateCandidate(payload)
      onSuccess(data)
      setIsLoading(false)
      addAlert({
        message: 'Candidate updated successfully',
        type: SnackbarType.Success,
      })
      handleClose()
    } catch (error: any) {
      addAlert({
        message: getApiErrorMsg(error),
        type: SnackbarType.Error,
      })
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Update Candidate</DialogTitle>
      <DialogContent dividers>
        <AvatarUpload
          onChangeMedia={handleChangeProfilePicture}
          isUploading={isProfilePictureUploading}
          src={getMediaUrl(formValues.profilePicture?.media || '')}
          onClearPhoto={handleRemoveProfilePicture}
        />
        <StyledTextField
          value={formValues.firstName}
          onChange={handleChangeFormField}
          label="First Name"
          name="firstName"
          error={formValues.invalid_firstName}
          helperText={formValues.invalid_firstName ? 'Required' : ''}
        />
        <StyledTextField
          value={formValues.lastName}
          onChange={handleChangeFormField}
          label="Last Name"
          name="lastName"
          error={formValues.invalid_lastName}
          helperText={formValues.invalid_lastName ? 'Required' : ''}
        />
        <StyledTextField
          value={formValues.email}
          onChange={handleChangeFormField}
          label="Email"
          name="email"
          error={formValues.invalid_email}
          helperText={
            formValues.invalid_email
              ? formValues.email.length === 0
                ? 'Required'
                : 'Invalid email'
              : ''
          }
        />
        <StyledTextField
          value={formValues.bio}
          onChange={handleChangeFormField}
          label="Bio"
          name="bio"
          multiline
          rows={4}
        />
        <StyledTextField
          value={formValues.notes}
          onChange={handleChangeFormField}
          label="Notes"
          name="notes"
          multiline
          rows={15}
        />
        <StyledTextField
          value={formValues.referral}
          onChange={handleChangeFormField}
          label="Referral"
          name="referral"
        />
        <StyledTextField
          value={formValues.source}
          onChange={handleChangeFormField}
          label="Source"
          name="source"
        />

        <StyledFormControl>
          <InputLabel id="state-select-label">Evaluation State</InputLabel>
          <Select
            labelId="state-select-label"
            id="state-select"
            value={formValues.evaluationState}
            label="Evaluation State"
            name="evaluationState"
            onChange={handleChangeSelect}
          >
            {Object.entries(EvaluationState).map(([key, value]) => (
              <MenuItem key={key} value={value}>
                {key}
              </MenuItem>
            ))}
          </Select>
        </StyledFormControl>
        <InfoItem label={"Warning"} value={"This will change the candidate's state forcefully without actually transitioning them through the process. If you need to transition them through a process use the update state button on the top right"} />
        <StyledFormControl>
          <InputLabel id="state-select-label">State</InputLabel>
          <Select
            labelId="state-select-label"
            id="state-select"
            value={formValues.state}
            label="State"
            name="state"
            onChange={handleChangeSelect}
          >
            {Object.entries(CandidateState).map(([key, value]) => (
              <MenuItem key={key} value={value}>
                {key}
              </MenuItem>
            ))}
          </Select>
        </StyledFormControl>

        <SectionTitle>Portfolio</SectionTitle>
        {formValues.portfolio.map((media, index) => (
          <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6">Portfolio Item {index + 1}</Typography>
              <IconButton onClick={() => handleRemovePortfolio(index)}>
                <DeleteIcon />
              </IconButton>
            </Box>
            <Typography sx={{ mt: 1 }}>
              {media.name}
            </Typography>
          </Box>
        ))}
        <MediaButtonBox>
          <LoadingButton
            variant="contained"
            component="label"
            sx={{ boxShadow: 'none' }}
            loading={isPortfolioUploading}
          >
            Add Portfolio Item
            <input
              type="file"
              hidden
              value={''}
              onChange={handlePortfolioMediaUpload}
              disabled={isPortfolioUploading}
              accept="image/*,video/*,application/pdf"
            />
          </LoadingButton>
          {isPortfolioUploading && (
            <UploadProgressIndicatorBox>
              <CircularProgressWithLabel value={uploadProgress} />
              <Typography variant="body2">
                {isPortfolioUploading && uploadProgress < 100
                  ? 'Uploading...'
                  : 'Processing...'}
              </Typography>
            </UploadProgressIndicatorBox>
          )}
        </MediaButtonBox>

        <SectionTitle>Assessment Responses</SectionTitle>
        {Object.entries(formValues.assessmentResponses).map(([key, value]) => (
          <Box key={key} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <TextField
                value={value.title}
                onChange={(e) => handleAssessmentResponseTitleChange(key, e.target.value)}
                label="Title"
                fullWidth
                sx={{ mr: 2 }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant={value.featured ? "contained" : "outlined"}
                  size="small"
                  onClick={() => handleToggleFeatured(key)}
                  sx={{ minWidth: '100px' }}
                >
                  {value.featured ? "Featured" : "Feature"}
                </Button>
                <IconButton onClick={() => handleRemoveAssessmentResponse(key)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
            {value.type === 'VIDEO' && (
              <>
                <MediaButtonBox>
                  <LoadingButton
                    variant="contained"
                    component="label"
                    sx={{ boxShadow: 'none' }}
                    loading={uploadingAssessmentResponse === key}
                  >
                    {value.videoResponse ? 'Change Video' : 'Add Video'}
                    <input
                      type="file"
                      hidden
                      value={''}
                      onChange={(e) => handleAssessmentResponseMediaUpload(e, key)}
                      disabled={uploadingAssessmentResponse === key}
                      accept="video/*"
                    />
                  </LoadingButton>
                  {uploadingAssessmentResponse === key && (
                    <UploadProgressIndicatorBox>
                      <CircularProgressWithLabel value={uploadProgress} />
                      <Typography variant="body2">
                        {uploadProgress < 100
                          ? 'Uploading...'
                          : 'Processing...'}
                      </Typography>
                    </UploadProgressIndicatorBox>
                  )}
                </MediaButtonBox>
                {value.videoResponse && (
                  <Typography sx={{ mt: 1 }}>
                    {value.videoResponse.name}
                  </Typography>
                )}
              </>
            )}
            {value.type === 'TEXT' && (
              <StyledTextField
                value={value.textResponse || ''}
                onChange={(e) => handleAssessmentResponseTextChange(key, e.target.value)}
                label="Response"
                multiline
                rows={4}
                fullWidth
              />
            )}
          </Box>
        ))}
        <Button
          startIcon={<AddIcon />}
          onClick={handleAddAssessmentResponse}
          sx={{ mb: 2 }}
        >
          Add Assessment Response
        </Button>

        <SectionTitle>Resume</SectionTitle>
        {formValues.resume && (
          <Typography sx={{ marginBottom: '10px' }}>
            {(formValues.resume as Media).name}
          </Typography>
        )}
        <MediaButtonBox>
          <LoadingButton
            variant="contained"
            component="label"
            sx={{ boxShadow: 'none' }}
            loading={isMediaUploading}
          >
            {formValues.resume ? 'Change Resume' : 'Add Resume'}
            <input
              type="file"
              hidden
              value={''}
              onChange={handleChangeUploadMedia}
              disabled={isMediaUploading}
              accept="application/pdf"
            />
          </LoadingButton>
          {isMediaUploading && (
            <UploadProgressIndicatorBox>
              <CircularProgressWithLabel value={uploadProgress} />
              <Typography variant="body2">
                {isMediaUploading && uploadProgress < 100
                  ? 'Uploading...'
                  : 'Processing...'}
              </Typography>
            </UploadProgressIndicatorBox>
          )}
        </MediaButtonBox>
        <StyledTextField
          value={formValues.resumeLink}
          onChange={handleChangeFormField}
          label="Resume Link"
          name="resumeLink"
        />
        <TextField
          type="number"
          value={formValues.expectedCompUSD}
          onChange={handleChangeFormField}
          label="Expected Compensation (USD)"
          name="expectedCompUSD"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <LoadingButton
          loading={loading}
          onClick={handleClickSave}
          disabled={isMediaUploading || isProfilePictureUploading || isPortfolioUploading || isAssessmentResponseUploading || uploadingAssessmentResponse !== null}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
